import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaFileDownload, FaTrash, FaUniversity } from 'react-icons/fa';
import { BsPeople } from 'react-icons/bs';
import { useToast } from '../contexts/ToastContext';
import { capitalAmortizationService } from '../services/capitalAmortizationService';
import {
  AmortizationOperation,
  AmortizationOperationRequest,
  CapitalAmortization,
  CapitalAmortizationUpdate,
  EstadoAmortizacion,
} from '../types/CapitalAmortization';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import ActionButton from '../components/common/ActionButton';
import ConfirmModal from '../components/common/ConfirmModal';
import OperacionForm from '../components/amortizaciones/OperacionForm';
import AmortizacionCard from '../components/amortizaciones/AmortizacionCard';
import { formatCurrency } from '../utils/format';
import { apiErrorMessage, exportOperacionCsv, fmtDate } from './amortizacionesUtils';
import './Amortizaciones.css';

type Filtro = 'Todas' | EstadoAmortizacion;
const FILTROS: Filtro[] = ['Todas', 'Propuesta', 'Acordada', 'Ejecutada', 'Descartada'];

const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

type Confirmacion =
  | { tipo: 'ejecutar' | 'anular'; amortizacion: CapitalAmortization }
  | { tipo: 'eliminar' };

const AmortizacionOperacion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [operacion, setOperacion] = useState<AmortizationOperation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('Todas');
  const [busqueda, setBusqueda] = useState('');
  const [confirmacion, setConfirmacion] = useState<Confirmacion | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await capitalAmortizationService.getOperacion(id!);
      setOperacion(res.data);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error al cargar la operación'), 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  /** Ejecuta una acción, muestra el resultado y recarga la operación (totales y tarjetas). */
  const run = async (action: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try {
      await action();
      showToast(ok);
      await load();
    } catch (err) {
      showToast(apiErrorMessage(err, fail), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateOperacion = (data: AmortizationOperationRequest) =>
    run(async () => {
      await capitalAmortizationService.updateOperacion(id!, data);
      setEditing(false);
    }, 'Operación actualizada', 'Error al actualizar la operación');

  const handleGenerar = () =>
    run(() => capitalAmortizationService.generarPropuestas(id!), 'Propuestas generadas', 'Error al generar las propuestas');

  const handleRemesa = async () => {
    setBusy(true);
    try {
      const res = await capitalAmortizationService.remesa(id!);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `remesa_amortizacion_${operacion?.productId ?? ''}_${operacion?.fechaCorte ?? ''}.xml`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      showToast('Remesa generada');
    } catch (err) {
      // Con responseType blob el mensaje de error del backend llega como Blob
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      let message: string | undefined;
      try {
        message = data instanceof Blob ? JSON.parse(await data.text()).message : undefined;
      } catch {
        message = undefined;
      }
      showToast(message || 'Error al generar la remesa', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = (amortizacionId: string, data: CapitalAmortizationUpdate) =>
    run(() => capitalAmortizationService.update(amortizacionId, data), 'Amortización guardada y recalculada', 'Error al guardar la amortización');

  const handleEstado = (amortizacionId: string, estado: EstadoAmortizacion) =>
    run(() => capitalAmortizationService.cambiarEstado(amortizacionId, estado), `Amortización marcada como ${estado.toLowerCase()}`, 'Error al cambiar el estado');

  const handleConfirm = async () => {
    const c = confirmacion;
    setConfirmacion(null);
    if (!c) return;
    if (c.tipo === 'ejecutar') {
      await run(() => capitalAmortizationService.ejecutar(c.amortizacion.id), 'Amortización ejecutada', 'Error al ejecutar la amortización');
    } else if (c.tipo === 'anular') {
      await run(() => capitalAmortizationService.anular(c.amortizacion.id), 'Ejecución anulada', 'Error al anular la ejecución');
    } else {
      setBusy(true);
      try {
        await capitalAmortizationService.deleteOperacion(id!);
        showToast('Operación eliminada');
        navigate('/amortizaciones');
      } catch (err) {
        showToast(apiErrorMessage(err, 'Error al eliminar la operación'), 'error');
        setBusy(false);
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!operacion) return null;

  const op = operacion;
  // Búsqueda por nombre del inversor o número de ficha, sin distinguir tildes ni mayúsculas
  const termino = normalizar(busqueda.trim());
  const encontradas = termino
    ? op.amortizaciones.filter(a => normalizar(a.investorName).includes(termino)
        || a.lines.some(l => normalizar(l.investmentSheetId).includes(termino)))
    : op.amortizaciones;
  const visibles = encontradas
    .filter(a => filtro === 'Todas' || a.estado === filtro)
    .sort((x, y) => x.investorName.localeCompare(y.investorName, 'es'));
  const objetivo = op.capitalObjetivo != null
    ? formatCurrency(op.capitalObjetivo)
    : op.porcentajeObjetivo != null ? `${(op.porcentajeObjetivo * 100).toLocaleString('es-ES', { maximumFractionDigits: 4 })} % por ficha` : '-';

  const confirmTexts = confirmacion?.tipo === 'ejecutar'
    ? {
        title: 'Ejecutar amortización',
        message: `Se registrará la salida de ${formatCurrency(confirmacion.amortizacion.capitalAmortizado)} de capital de ${confirmacion.amortizacion.investorName} `
          + `y la liquidación de ${formatCurrency(confirmacion.amortizacion.pendienteBruto)} de intereses brutos a ${fmtDate(confirmacion.amortizacion.fechaCorte)}. `
          + 'Desde ese trimestre los intereses a cuenta se calcularán sobre el capital restante.',
        confirmText: 'Ejecutar',
        type: 'warning' as const,
      }
    : confirmacion?.tipo === 'anular'
      ? {
          title: 'Anular ejecución',
          message: `Se darán de baja los movimientos de la amortización de ${confirmacion.amortizacion.investorName} y sus fichas recuperarán el capital. La amortización volverá a "Acordada".`,
          confirmText: 'Anular ejecución',
          type: 'danger' as const,
        }
      : {
          title: 'Eliminar operación',
          message: 'Se eliminará la operación y todas sus amortizaciones no ejecutadas.',
          confirmText: 'Eliminar',
          type: 'danger' as const,
        };

  return (
    <div className="amz-page">
      <div className="amz-detail-header">
        <ActionButton icon={FaArrowLeft} text="Volver" variant="detail" onClick={() => navigate('/amortizaciones')} />
        <h2>{op.descripcion}</h2>
      </div>

      {editing ? (
        <div className="amz-panel">
          <h3>Editar operación</h3>
          <p className="amz-hint">Los cambios solo afectan a las propuestas que se generen después; las amortizaciones existentes no se modifican.</p>
          <OperacionForm operacion={op} saving={busy} onSubmit={handleUpdateOperacion} onCancel={() => setEditing(false)} />
        </div>
      ) : (
        <div className="amz-panel">
          <div className="amz-meta">
            <div><span>Producto</span><strong>{op.productId} · {op.productName}</strong></div>
            <div><span>Fecha de corte</span><strong>{fmtDate(op.fechaCorte)}</strong></div>
            <div><span>Devolución de referencia</span><strong>{op.fechaDevolucionRef ? fmtDate(op.fechaDevolucionRef) : 'Estimada inicial de cada fase'}</strong></div>
            <div><span>Objetivo</span><strong>{objetivo}</strong></div>
            <div><span>Redondeo</span><strong>{formatCurrency(op.redondeo, 0)}</strong></div>
            {op.destinoReinversion && <div><span>Reinversión en</span><strong>{op.destinoReinversion}</strong></div>}
          </div>
          {op.notas && <p className="amz-notas">{op.notas}</p>}
          <div className="amz-summary">
            <div><span>Capital vivo afectado</span><strong>{formatCurrency(op.capitalPrevio)}</strong></div>
            <div><span>En propuesta</span><strong>{formatCurrency(op.capitalPropuesto)}</strong></div>
            <div><span>Acordado</span><strong>{formatCurrency(op.capitalAcordado)}</strong></div>
            <div><span>Ejecutado</span><strong>{formatCurrency(op.capitalEjecutado)}</strong></div>
            <div><span>Neto a pagar (acordado)</span><strong>{formatCurrency(op.netoAcordado)}</strong></div>
            {op.diferenciaObjetivo != null && (
              <div className={op.diferenciaObjetivo !== 0 ? 'amz-summary-warn' : ''}>
                <span>Diferencia con el objetivo</span><strong>{formatCurrency(op.diferenciaObjetivo)}</strong>
              </div>
            )}
          </div>
          <div className="amz-toolbar">
            <ActionButton icon={BsPeople} text="Generar propuestas" variant="primary" disabled={busy} onClick={handleGenerar} />
            <ActionButton icon={FaFileDownload} text="Exportar CSV" variant="metrics" disabled={op.amortizaciones.length === 0}
                          onClick={() => exportOperacionCsv(op)} />
            <ActionButton icon={FaUniversity} text="Remesa SEPA de las ejecutadas" variant="detail"
                          disabled={busy || op.numEjecutadas === 0} onClick={handleRemesa} />
            <ActionButton icon={FaEdit} text="Editar" variant="edit" disabled={busy} onClick={() => setEditing(true)} />
            <ActionButton icon={FaTrash} text="Eliminar" variant="delete" disabled={busy || op.numEjecutadas > 0}
                          onClick={() => setConfirmacion({ tipo: 'eliminar' })} />
          </div>
        </div>
      )}

      {op.amortizaciones.length === 0 ? (
        <EmptyState icon={BsPeople} title="Sin amortizaciones"
                    subtitle="Pulsa «Generar propuestas» para crear una propuesta por cada inversor con capital vivo en el producto." />
      ) : (
        <>
          <div className="amz-filters">
            <input type="search" className="amz-search" placeholder="Buscar inversor o ficha…" value={busqueda}
                   aria-label="Buscar inversor o ficha" onChange={e => setBusqueda(e.target.value)} />
            {FILTROS.map(f => {
              const n = f === 'Todas' ? encontradas.length : encontradas.filter(a => a.estado === f).length;
              return (
                <button key={f} type="button" className={`amz-filter ${filtro === f ? 'active' : ''}`} onClick={() => setFiltro(f)}>
                  {f} ({n})
                </button>
              );
            })}
          </div>
          {visibles.length === 0 && (
            <p className="amz-muted">Ningún inversor coincide con la búsqueda y el filtro seleccionados.</p>
          )}
          {visibles.map(a => (
            <AmortizacionCard key={a.id} amortizacion={a} busy={busy}
                              onSave={handleSave} onEstado={handleEstado}
                              onEjecutar={x => setConfirmacion({ tipo: 'ejecutar', amortizacion: x })}
                              onAnular={x => setConfirmacion({ tipo: 'anular', amortizacion: x })} />
          ))}
        </>
      )}

      <ConfirmModal isOpen={confirmacion !== null} title={confirmTexts.title} message={confirmTexts.message}
                    confirmText={confirmTexts.confirmText} type={confirmTexts.type}
                    onConfirm={handleConfirm} onCancel={() => setConfirmacion(null)} />
    </div>
  );
};

export default AmortizacionOperacion;
